<script setup>
/** 一个回合的时间线：关键指标、时间轴主图、最长的几段、全部节点明细。 */
import { computed, ref, watch } from 'vue'
import { ArrowLeft } from '@lucide/vue'
import { Badge, Button, EmptyState, PageHeader, Section, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getTurnTimeline } from '../../api/llm/index.js'
import { formatDateTime } from './llm-format.js'
import { MOMENT_METRICS, formatMoment, formatSeconds } from './turn-timeline/timeline-layout.js'
import TurnTimelineChart from './turn-timeline/TurnTimelineChart.vue'
import TurnTimelineSpans from './turn-timeline/TurnTimelineSpans.vue'
import TurnTimelineMetrics from './turn-timeline/TurnTimelineMetrics.vue'
import TurnTimelineNodeTable from './turn-timeline/TurnTimelineNodeTable.vue'

const props = defineProps({
  turnId: { type: String, required: true },
})

const METRIC_KEYS = ['longest_gap', 'idle_total', 'first_feedback', 'npc_first_text', 'npc_first_sound', 'input_unlocked']

const { t } = useI18n()
const router = useRouter()
const timeline = ref(null)
const loading = ref(false)
const error = ref('')

const alignmentNote = computed(() => {
  if (!timeline.value.has_client_timeline) return t('visualGameAdmin.llm.turnTimelines.detail.noClient')
  if (timeline.value.offset_event === null) return t('visualGameAdmin.llm.turnTimelines.detail.unaligned')
  return t('visualGameAdmin.llm.turnTimelines.detail.aligned', { ms: timeline.value.offset_ms, event: timeline.value.offset_event })
})

const metricItems = computed(() => METRIC_KEYS.map(key => {
  const metric = timeline.value.metrics?.[key] ?? null
  return {
    key,
    label: t(`visualGameAdmin.llm.turnTimelines.metrics.${key}`),
    value: metric === null ? '—' : MOMENT_METRICS.includes(key) ? formatMoment(metric.ms) : formatSeconds(metric.ms),
    hint: metric === null
      ? t('visualGameAdmin.llm.turnTimelines.metrics.missing')
      : key === 'idle_total' ? t('visualGameAdmin.llm.turnTimelines.metrics.gapCount', { count: metric.node }) : metric.node,
  }
}))

async function load() {
  loading.value = true
  error.value = ''
  try {
    timeline.value = await getTurnTimeline(props.turnId)
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    loading.value = false
  }
}

watch(() => props.turnId, load, { immediate: true })
</script>

<template>
  <section class="turn-timeline-detail vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.turnTimelines.detail.title')">
      <template #actions>
        <Button variant="secondary" size="sm" @click="router.push({ name: 'llm-turn-timelines' })">
          <ArrowLeft :size="14" />
          {{ t('visualGameAdmin.llm.turnTimelines.detail.back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="vga-state">
      <Spinner size="lg" :label="t('visualGameAdmin.common.loading')" />
    </div>

    <div v-else-if="error" class="vga-state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <template v-else-if="timeline !== null">
      <Section>
        <div class="turn-summary">
          <div class="turn-summary__line">
            <strong>{{ timeline.turn.scenario_title }}</strong>
            <Badge :variant="timeline.turn.mode === 'say' ? 'info' : 'warning'">{{ t(`visualGameAdmin.llm.turnTimelines.modes.${timeline.turn.mode}`) }}</Badge>
            <span>{{ t('visualGameAdmin.llm.turnTimelines.detail.turnNo', { no: timeline.turn.turn_no }) }}</span>
            <span>{{ formatDateTime(timeline.turn.created_at) }}</span>
          </div>
          <p class="turn-summary__input">{{ timeline.turn.input_text }}</p>
          <p class="vga-mono turn-summary__ids">{{ timeline.turn.id }} · {{ timeline.turn.run_id }}</p>
        </div>
      </Section>

      <EmptyState
        v-if="!timeline.recorded"
        :title="t('visualGameAdmin.llm.turnTimelines.detail.notRecordedTitle')"
        :description="t('visualGameAdmin.llm.turnTimelines.detail.notRecordedDescription')"
      />

      <template v-else>
        <p class="turn-note" data-testid="turn-timeline-alignment">
          {{ alignmentNote }}
        </p>

        <TurnTimelineMetrics :items="metricItems" />

        <Section :title="t('visualGameAdmin.llm.turnTimelines.detail.chartTitle')">
          <TurnTimelineChart :timeline="timeline" />
        </Section>

        <div class="turn-columns">
          <Section :title="t('visualGameAdmin.llm.turnTimelines.spans.title')">
            <TurnTimelineSpans :timeline="timeline" />
          </Section>
          <Section :title="t('visualGameAdmin.llm.turnTimelines.nodes.title')">
            <TurnTimelineNodeTable :nodes="timeline.nodes" />
          </Section>
        </div>
      </template>
    </template>
  </section>
</template>

<style scoped>
.turn-summary {
  display: grid;
  gap: var(--drama-space-sm);
}

.turn-summary__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm) var(--drama-space-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.turn-summary__line strong {
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-md);
}

.turn-summary__input {
  margin: 0;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-lg);
}

.turn-summary__ids,
.turn-note {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.turn-columns {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
  gap: var(--drama-space-lg);
  align-items: start;
}

@media (max-width: 1080px) {
  .turn-columns {
    grid-template-columns: 1fr;
  }
}
</style>
